import { Activity, BarChart3, Eye, History, Mail, PenLine, Settings, Share2 } from 'lucide-react';
import { formatSurveyNumber } from '@survey/core';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { Permission, SurveyStatus } from '../api/types';
import { useAuth } from '../auth';
import { useI18n } from '../i18n';
import { StatusPill } from './ui';

export type SurveyTab = 'build' | 'settings' | 'preview' | 'share' | 'invites' | 'responses' | 'field' | 'activity';

interface Props {
  id: string;
  number: number;
  code: string;
  status: SurveyStatus;
  permissions: Permission[];
  active: SurveyTab;
  title: ReactNode;
  actions?: ReactNode;
  meta?: ReactNode;
}

export function SurveyHeader({ id, number, code, status, permissions, active, title, actions, meta }: Props) {
  const { t } = useI18n();
  // Same rule as the API: editors of this questionnaire, or roles that may read the audit log.
  const canAudit = useAuth().can('audit.view');
  const tabs: { key: SurveyTab; label: string; icon: ReactNode; to: string; show: boolean }[] = [
    { key: 'build', label: t('tabs.build'), icon: <PenLine />, to: `/surveys/${id}`, show: permissions.includes('survey.edit') },
    { key: 'settings', label: t('tabs.settings'), icon: <Settings />, to: `/surveys/${id}/settings`, show: permissions.includes('survey.edit') },
    { key: 'preview', label: t('tabs.preview'), icon: <Eye />, to: `/surveys/${id}/preview`, show: permissions.includes('survey.edit') },
    { key: 'share', label: t('tabs.share'), icon: <Share2 />, to: `/surveys/${id}/share`, show: true },
    { key: 'invites', label: t('tabs.invites'), icon: <Mail />, to: `/surveys/${id}/invites`, show: permissions.includes('survey.share') },
    { key: 'responses', label: t('tabs.responses'), icon: <BarChart3 />, to: `/surveys/${id}/responses`, show: permissions.includes('responses.view') },
    {
      key: 'field',
      label: t('tabs.field'),
      icon: <Activity />,
      to: `/surveys/${id}/${permissions.includes('responses.view') ? 'progress' : 'assignments'}`,
      show: permissions.includes('responses.view') || permissions.includes('survey.enumerators.assign'),
    },
    { key: 'activity', label: t('tabs.activity'), icon: <History />, to: `/surveys/${id}/activity`, show: permissions.includes('survey.edit') || canAudit },
  ];
  return (
    <div className="mb-16">
      <nav className="breadcrumb" aria-label={t('nav.breadcrumb')}>
        <Link to="/surveys">{t('nav.questionnaires')}</Link>
        <span>/</span>
        <span className="number-tag">{formatSurveyNumber(number)}</span>
        <span className="code" title={t('header.code_title')}>{code}</span>
        <StatusPill status={status} />
        {meta}
      </nav>
      <div className="topbar" style={{ marginBottom: 14 }}>
        <div className="grow" style={{ minWidth: 200 }}>
          {title}
        </div>
        {actions}
      </div>
      <div className="tabs" role="tablist">
        {tabs
          .filter((t) => t.show)
          .map((t) => (
            <Link key={t.key} to={t.to} role="tab" aria-selected={active === t.key} className={`tab ${active === t.key ? 'active' : ''}`}>
              {t.icon}
              {t.label}
            </Link>
          ))}
      </div>
    </div>
  );
}

/** Second-level switch inside the Field tab: live progress and who collects what. */
export function FieldNav({ id, permissions, active }: { id: string; permissions: Permission[]; active: 'progress' | 'assignments' }) {
  const { t } = useI18n();
  const items = [
    {
      key: 'progress',
      label: t('field.nav_progress'),
      show: permissions.includes('responses.view'),
    },
    {
      key: 'assignments',
      label: t('field.nav_assignments'),
      show: permissions.includes('survey.enumerators.assign'),
    },
  ].filter((i) => i.show);
  if (items.length < 2) return null;
  return (
    <nav className="seg mb-16" aria-label={t('tabs.field')}>
      {items.map((i) => (
        <Link key={i.key} to={`/surveys/${id}/${i.key}`} className={active === i.key ? 'active' : ''} aria-current={active === i.key ? 'page' : undefined}>
          {i.label}
        </Link>
      ))}
    </nav>
  );
}
